<script setup lang="ts">
import GeoJSON from 'ol/format/GeoJSON'
import { Feature } from 'ol'
import Point from 'ol/geom/Point'
import Geometry from 'ol/geom/Geometry'
import WebGLPointsLayer from 'ol/layer/WebGLPoints'
import VectorSource from 'ol/source/Vector'
import { colorsForSymbols } from '~/composables/colors'
import { LayerIdentifier, LayerLegend, Sprite } from '~/types/map'
const props = defineProps({
  features: { type: Array, required: true },
  symbolOverrides: { type: Object, default: () => ({}) },
})
const geoJson = new GeoJSON({
  featureProjection: 'EPSG:3857',
})
const features = computed(() => props.features as Feature<Point>[])

watch(
  () => props.features,
  () => {
    refreshFeatures()
    updateLayerLegend()
  }
)

const layer = ref<{ webglPointsLayer: WebGLPointsLayer<VectorSource<Point>> }>()
watch(layer, () => {
  if (!layer.value) return
  const vectorSource = new VectorSource({
    features: [...props.features] as Feature<Geometry>[],
    format: geoJson,
  })
  layer.value.webglPointsLayer.setSource(vectorSource as VectorSource<Point>)
  layer.value.webglPointsLayer.setVisible(visible.value)
  refreshFeatures()
})

const { visible } = inject<{ visible: Ref<boolean> }>('layerControl', {
  visible: ref(false),
})
watch(visible, (newValue) => {
  layer.value && layer.value.webglPointsLayer.setVisible(newValue)
})

function refreshFeatures() {
  if (!layer.value || !visible.value) return

  const layerSource = layer.value.webglPointsLayer.getSource()
  if (!layerSource) return
  layerSource.getFeatures().forEach((feature) => {
    layerSource.removeFeature(feature)
  })

  features.value.forEach((feature) => {
    layerSource.addFeature(feature)
    feature.changed()
  })
  layerSource.changed()
  layer.value.webglPointsLayer.changed()
}

const minSize = 10
const maxSize = 36
const spriteSource = '/images/map_icons.png'
const spriteSize = 96
const spriteImageSize = 384
const spriteIcons: { [key: string]: Sprite } = {
  electricity: {
    coords: [0.25, 0.25, 0.5, 0.5],
    label: 'El',
  },
  gas: {
    coords: [0.25, 0, 0.5, 0.25],
    label: 'Fossilfri gas',
  },
  liquid: {
    coords: [0.75, 0.25, 1, 0.5],
    label: 'Flytande fossilfritt',
  },
  mixed: {
    coords: [0.25, 0.5, 0.5, 0.75],
    label: 'Station med flertal drivmedel',
  },
  unknown: {
    coords: [0.75, 0.75, 1, 1],
    label: 'Okänt',
  },
  supply: {
    coords: [0.75, 0, 1, 0.25],
    label: 'Utbud',
  },
  demand: {
    coords: [0.5, 0, 0.75, 0.25],
    label: 'Efterfrågan',
  },
}

const symbolColors = {
  existing: { color: colorsForSymbols.existing, label: 'Befintlig station' },
  planned: { color: colorsForSymbols.planned, label: 'Planerad station' },
  supply: { color: colorsForSymbols.supply, label: 'Utbud' },
  demand: { color: colorsForSymbols.demand, label: 'Efterfrågan' },
}
const webglSpritPointsStyle = {
  symbol: {
    symbolType: 'image',
    src: spriteSource,
    offset: ['array', 0, 0],
    textureCoord: [
      'case',
      ['==', ['get', 'fuelTypeGroup'], 'electricity'],
      spriteIcons.electricity.coords,
      ['==', ['get', 'fuelTypeGroup'], 'gas'],
      spriteIcons.gas.coords,
      ['==', ['get', 'fuelTypeGroup'], 'liquid'],
      spriteIcons.liquid.coords,
      ['==', ['get', 'fuelTypeGroup'], 'mixed'],
      spriteIcons.mixed.coords,
      ['==', ['get', 'fuelTypeGroup'], 'unknown'],
      spriteIcons.unknown.coords,
      ['==', ['get', 'type'], 'demand'],
      spriteIcons.demand.coords,
      ['==', ['get', 'type'], 'supply'],
      spriteIcons.supply.coords,
      [0.25, 0.25, 0.5, 0.5],
    ],
    size: ['interpolate', ['exponential', 2.5], ['zoom'], 2, minSize, 14, maxSize],
    color: [
      'case',
      ['==', ['get', 'selected'], '1'],
      '#E94E1B',
      ['==', ['get', 'hover'], '1'],
      '#F39200',
      ['==', ['get', 'type'], 'existing'],
      symbolColors.existing.color,
      ['==', ['get', 'type'], 'planned'],
      symbolColors.planned.color,
      ['==', ['get', 'type'], 'demand'],
      symbolColors.demand.color,
      ['==', ['get', 'type'], 'supply'],
      symbolColors.supply.color,
      ['==', ['get', 'fuelTypeGroup'], 'liquid'],
      '#65D784',
      '#65D784',
    ],
    rotateWithView: false,
    opacity: ['match', ['get', 'type'], 'existing', 1, 'planned', 1, 1],
    ...props.symbolOverrides,
  },
}

const layerControl = inject<{ name: string; dataLegendLabel: string; identifier: LayerIdentifier }>(
  'layerControl'
)
const activeIconTypes = computed(() => {
  const iconIdentifiers: string[] = []
  features.value.forEach((feature) => {
    const fuelTypeGroup = feature.get('fuelTypeGroup')
    const type = feature.get('type')
    if (!fuelTypeGroup && !type) return
    if (!iconIdentifiers.includes(fuelTypeGroup)) {
      iconIdentifiers.push(fuelTypeGroup)
    }
    if (!iconIdentifiers.includes(type)) {
      iconIdentifiers.push(type)
    }
  })
  return iconIdentifiers
})

const activeColors = computed(() => {
  const colorIdentifiers: string[] = []
  features.value.forEach((feature) => {
    const type = feature.get('type')
    if (!type) return
    if (!colorIdentifiers.includes(type)) {
      colorIdentifiers.push(type)
    }
  })
  return colorIdentifiers
})

const layerLegends = inject<{
  registerLegend: (legend: LayerLegend) => void
}>('layerLegends')

function filterActiveLegendItems(
  itemsDefiniton: { [key: string]: any },
  activeItemsIdentifiers: string[]
) {
  return Object.keys(itemsDefiniton).reduce((acc, key) => {
    if (activeItemsIdentifiers.includes(key)) acc[key] = itemsDefiniton[key]
    return acc
  }, {} as { [key: string]: any })
}

function updateLayerLegend() {
  if (!layerLegends) return
  layerLegends.registerLegend({
    name: (layerControl && layerControl.name) || 'Drivmedelsstationer',
    dataLegendLabel: layerControl && layerControl.dataLegendLabel,
    identifier: (layerControl && layerControl.identifier) || 'stations',
    type: 'sprite',
    visible,
    spriteData: {
      spriteImage: spriteSource,
      spriteSize,
      spriteImageSize,
      sprites: filterActiveLegendItems(spriteIcons, activeIconTypes.value),
      colors: filterActiveLegendItems(symbolColors, activeColors.value),
    },
  })
}

onMounted(() => {
  updateLayerLegend()
})
</script>
<template>
  <ol-webglpoints-layer
    ref="layer"
    :class-name="'webglpointlayer'"
    :styles="webglSpritPointsStyle"
    :z-index="3"
  ></ol-webglpoints-layer>
</template>
